<template>
  <div class="login-view">
    <div class="login-container">
      <div class="login-card">
        <div class="login-header">
          <div class="login-icon">
            <svg xmlns="http://www.w3.org/2000/svg" class="microsoft-icon" viewBox="0 0 23 23">
              <path fill="#f25022" d="M0 0h11v11H0z" />
              <path fill="#00a4ef" d="M12 0h11v11H12z" />
              <path fill="#7fba00" d="M0 12h11v11H0z" />
              <path fill="#ffb900" d="M12 12h11v11H12z" />
            </svg>
          </div>
          <h1 class="login-title">Welcome to RapID Audit</h1>
          <p class="login-subtitle">Sign in with your Microsoft account to continue</p>
        </div>

        <div class="login-content">
          <div v-if="error" class="error-message" role="alert">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="error-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{{ error }}</span>
            <button @click="retryLogin" class="retry-button">Try Again</button>
          </div>
          <div v-else-if="isLoading" class="loading-spinner">
            <svg class="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p class="loading-text">Redirecting to Microsoft...</p>
          </div>
          <div v-else class="login-button-container">
            <button
              @click="handleO365Login"
              type="button"
              class="login-button"
              :disabled="isLoading"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="microsoft-icon-small"
                viewBox="0 0 23 23"
              >
                <path fill="#f25022" d="M0 0h11v11H0z" />
                <path fill="#00a4ef" d="M12 0h11v11H12z" />
                <path fill="#7fba00" d="M0 12h11v11H0z" />
                <path fill="#ffb900" d="M12 12h11v11H12z" />
              </svg>
              <span>Sign in with Microsoft</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { loginWithO365 } from '../../services/oauth.service';
import { consumeAuthRedirectPath, setAuthRedirectPath } from '../../services/auth-redirect.service';
import { useAuthStore } from '../../stores/auth.store';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const error = ref<string>('');
const isLoading = ref(false);

async function handleO365Login(event?: Event) {
  // Prevent default form submission behavior
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  try {
    error.value = '';
    isLoading.value = true;

    // Debug: Check environment variables
    console.log('[LoginView] Environment check:', {
      cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN
        ? `${import.meta.env.VITE_COGNITO_DOMAIN.substring(0, 20)}...`
        : 'MISSING',
      cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID
        ? `${import.meta.env.VITE_COGNITO_CLIENT_ID.substring(0, 8)}...`
        : 'MISSING',
      o365Provider: import.meta.env.VITE_O365_PROVIDER_NAME || 'MISSING',
      origin: window.location.origin,
    });

    await loginWithO365();
    // Note: loginWithO365() redirects, so this code won't execute
  } catch (err) {
    isLoading.value = false;
    const errorMessage =
      err instanceof Error
        ? err.message
        : 'Failed to redirect to Microsoft login. Please try again.';
    error.value = errorMessage;
    console.error('[LoginView] O365 login failed:', err);
    console.error('[LoginView] Error details:', {
      message: errorMessage,
      stack: err instanceof Error ? err.stack : undefined,
    });
  }
}

function retryLogin() {
  handleO365Login();
}

onMounted(() => {
  authStore.initializeAuth();
  const redirectPath = typeof route.query.redirect === 'string' ? route.query.redirect : null;

  setAuthRedirectPath(redirectPath);

  if (authStore.isAuthenticated && authStore.isTokenValid) {
    router.replace(consumeAuthRedirectPath());
    return;
  }
  // Don't auto-redirect - let user click the button
});
</script>

<style scoped>
.login-view {
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.login-container {
  width: 100%;
  max-width: 440px;
}

.login-card {
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.4s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.login-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  background: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.login-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.5px;
}

.login-subtitle {
  color: #718096;
  font-size: 0.95rem;
  margin: 0;
}

.login-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 2rem;
}

.login-button-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.login-button {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: #0078d4;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 120, 212, 0.3);
}

.login-button:hover:not(:disabled) {
  background: #006cbe;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 120, 212, 0.4);
}

.login-button:active:not(:disabled) {
  transform: translateY(0);
}

.login-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.microsoft-icon-small {
  width: 24px;
  height: 24px;
}

.loading-text {
  margin-top: 1rem;
  color: #718096;
  font-size: 0.9rem;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.microsoft-icon {
  width: 48px;
  height: 48px;
}

.spinner {
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.error-message {
  background-color: #fef2f2;
  border: 2px solid #fecaca;
  color: #dc2626;
  padding: 1.5rem;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  font-size: 0.9rem;
  animation: shake 0.4s ease;
  text-align: center;
  max-width: 400px;
}

.retry-button {
  padding: 0.75rem 1.5rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-button:hover {
  background: #b91c1c;
  transform: translateY(-1px);
}

.retry-button:active {
  transform: translateY(0);
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-8px);
  }
  75% {
    transform: translateX(8px);
  }
}

.error-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .login-view {
    padding: 1rem;
  }

  .login-card {
    padding: 2rem;
  }

  .login-title {
    font-size: 1.75rem;
  }
}
</style>
